import { getIntlLocale } from "@/i18n";

/** Browser-local calendar date as `YYYY-MM-DD` (for `<input type="date">`). */
export function todayDateInputValue(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Date only (default: day 2-digit, month short, year numeric). */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  };
  return new Date(date).toLocaleDateString(getIntlLocale(), defaultOptions);
}

/** Date + time in the user's i18n locale (local wall clock). */
export function formatDateTime(date: string | Date): string {
  try {
    return new Date(date).toLocaleString(getIntlLocale(), {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(date);
  }
}

/** Time only in the user's i18n locale (local wall clock). */
export function formatTime(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Date(date).toLocaleTimeString(getIntlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
}

/** Truncate a schedule time string (`HH:MM:SS` or `HH:MM`) to `HH:MM`. */
export function hhmmFromTimeString(time: string): string {
  return time.slice(0, 5);
}

/** Relative past time (e.g. "14 minutes ago"). */
export function formatRelativeTime(date: string | Date): string {
  const then = new Date(date).getTime();
  const diffSec = Math.floor((Date.now() - then) / 1000);
  const locale = getIntlLocale();

  if (diffSec < 45) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      0,
      "second",
    );
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always" });

  if (diffSec < 3600) {
    return rtf.format(-Math.floor(diffSec / 60), "minute");
  }
  if (diffSec < 86400) {
    return rtf.format(-Math.floor(diffSec / 3600), "hour");
  }
  if (diffSec < 604800) {
    return rtf.format(-Math.floor(diffSec / 86400), "day");
  }
  if (diffSec < 2_592_000) {
    return rtf.format(-Math.floor(diffSec / 604800), "week");
  }
  if (diffSec < 31_536_000) {
    return rtf.format(-Math.floor(diffSec / 2_592_000), "month");
  }
  return rtf.format(-Math.floor(diffSec / 31_536_000), "year");
}
